// Belege nach der Zustellung – Finanzen (docs/belege-nach-zustellung/SPEZIFIKATION.md §3–§6):
//   * Schalter je Betrieb (alle standardmäßig AUS): Rechnung nach der Zustellung automatisch erstellen,
//     Sammelrechnungen automatisch erstellen, automatisch erstellte Rechnungen per E-Mail senden (mit Zustimmung für
//     Rechnungen ohne E-Rechnung).
//   * Rechnung und Rechnungskorrektur als PDF zum Herunterladen.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zDocumentPdf } from '../../sales/contract'

const AUTOMATION_PERMISSION = 'finance.automation.manage'

const zSchedule = z.object({
  /** nächster Fälligkeitstag */
  nextDueDate: z.string(),
  /** Stichtag des letzten fälligen Laufs (Lieferungen bis einschließlich) */
  until: z.string(),
})

export const zFinanceAutomation = z.object({
  /** Rechnung nach der Zustellung automatisch erstellen und festschreiben (Kunden ohne Sammelrechnung, an der Tür bezahlt) */
  autoInvoice: z.boolean(),
  /** fällige Sammelrechnungen automatisch erstellen (wöchentlich montags, 14-täglich montags, monatlich am 1.) */
  autoCollective: z.boolean(),
  /** automatisch erstellte Rechnungen per E-Mail senden (PDF, E-Rechnung falls vorhanden) */
  autoSendInvoice: z.boolean(),
  /** Kunden ohne E-Rechnung haben dem Rechnungsversand per E-Mail zugestimmt */
  sendConsent: z.boolean(),
  version: z.int(),
  updatedAt: z.date().nullable(),
  /** Zeitplan der Sammelrechnung, von heute aus */
  schedule: z.object({ weekly: zSchedule, biweekly: zSchedule, monthly: zSchedule }),
  /** Kunden mit Sammelrechnung (aktiv) */
  collectiveCustomers: z.int(),
})
export type FinanceAutomation = z.output<typeof zFinanceAutomation>

export const financeAutomationGet = defineContract({
  name: 'finance.automation.get',
  title: 'Rechnungsautomatik anzeigen',
  description: 'Ob Rechnungen nach der Zustellung und fällige Sammelrechnungen automatisch entstehen und per E-Mail hinausgehen.',
  llm: {
    description:
      'Liefert die Schalter des Betriebs: Rechnung nach der Zustellung automatisch erstellen, Sammelrechnungen automatisch erstellen, ' +
      'automatisch erstellte Rechnungen per E-Mail senden (und ob Kunden ohne E-Rechnung zugestimmt haben) – dazu den Zeitplan der ' +
      'Sammelrechnungen. Ändert nichts; geändert wird mit finance.automation.update.',
    keywords: ['Rechnung automatisch', 'Sammelrechnung', 'Rechnung per E-Mail', 'Automatik'],
  },
  input: z.strictObject({}),
  output: zFinanceAutomation,
  permission: AUTOMATION_PERMISSION,
  risk: 'read',
})

export const financeAutomationUpdate = defineContract({
  name: 'finance.automation.update',
  title: 'Rechnungsautomatik einstellen',
  description: 'Schaltet die automatische Rechnung nach der Zustellung, die automatischen Sammelrechnungen und den automatischen Versand ein oder aus.',
  llm: {
    description:
      'Schaltet je Betrieb: `autoInvoice` (Rechnung nach der Zustellung – nur Kunden ohne Sammelrechnung und an der Tür bezahlte ' +
      'Lieferungen; was erst nach der Rücknahme abgerechnet wird, nie), `autoCollective` (fällige Sammelrechnungen), ' +
      '`autoSendInvoice` (automatisch erstellte ' +
      'Rechnungen per E-Mail) und `sendConsent` (Kunden ohne E-Rechnung sind mit Rechnungen per E-Mail einverstanden). Ohne Angabe bleibt ' +
      'ein Schalter, wie er ist.',
    keywords: ['Rechnung automatisch erstellen', 'Sammelrechnung automatisch', 'Rechnung automatisch senden'],
  },
  input: z.strictObject({
    autoInvoice: z.boolean().optional(),
    autoCollective: z.boolean().optional(),
    autoSendInvoice: z.boolean().optional(),
    sendConsent: z.boolean().optional(),
    expectedVersion: z.int().optional(),
  }),
  output: zFinanceAutomation,
  permission: AUTOMATION_PERMISSION,
  risk: 'write',
  assistant: 'never',
})

export const invoicePdf = defineContract({
  name: 'finance.invoice.pdf',
  title: 'Rechnung als PDF',
  description: 'Erzeugt die Rechnung als PDF – wie der Druck.',
  llm: {
    description: 'Erzeugt die Rechnung `id` als PDF (Base64) zum Herunterladen. Ändert nichts und legt nichts ab. Bei E-Rechnungen bleibt die XML-Datei maßgeblich.',
    keywords: ['PDF', 'Rechnung herunterladen', 'Rechnung als Datei'],
  },
  input: z.strictObject({ id: zId }),
  output: zDocumentPdf,
  permission: 'finance.invoice.read',
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})

export const creditNotePdf = defineContract({
  name: 'finance.creditNote.pdf',
  title: 'Rechnungskorrektur als PDF',
  description: 'Erzeugt die Rechnungskorrektur als PDF – wie der Druck.',
  llm: {
    description: 'Erzeugt die Rechnungskorrektur `id` als PDF (Base64) zum Herunterladen. Ändert nichts und legt nichts ab.',
    keywords: ['PDF', 'Gutschrift herunterladen', 'Rechnungskorrektur als Datei'],
  },
  input: z.strictObject({ id: zId }),
  output: zDocumentPdf,
  permission: 'finance.invoice.read',
  risk: 'read',
  scope: 'record',
  assistant: 'never',
})
